/* eslint-disable no-console, no-debugger */

import Base from "+/Engine/Base";

import { markRaw } from "vue";
import EntityStore from "+/Engine/EntityStore";
import Entity from "+/Engine/Entity";
import FeedStore from "+/Engine/FeedStore";
import Event from "+/Engine/Event";
import { BaseFrameworkError, compareTwoObjects } from "@bloglovin/vue-framework";

// Routines go here -- the import triggers registration which needs to happen before they are referenced
/* eslint-disable no-unused-vars */
import CreateEntity from "+/Engine/Routines/CreateEntity";
import UpdateEntity from "+/Engine/Routines/UpdateEntity";
import UpdateEntities from "+/Engine/Routines/UpdateEntities";
import GetEntity from "+/Engine/Routines/GetEntity";
import GetEntities from "+/Engine/Routines/GetEntities";
import DeleteEntity from "+/Engine/Routines/DeleteEntity";
import TriggerEvent from "+/Engine/Routines/TriggerEvent";
import Foreach from "+/Engine/Routines/Foreach";
/* eslint-enable no-unused-vars */
import { Context } from "%/dynamic-components/render-engine/classes-v2/context";
// eslint-disable-next-line no-unused-vars
import DataTypes from "+/Engine/Helpers/DataTypes";

const MAX_TRANSACTION_LAG_BEFORE_RESET = 500;

export default class App extends Base {

  // -------------------//
  // Static Class Vars //
  // -------------------//

  static #instances = {};
  static #privateConstructorToken = Symbol("privateConstructorToken");

  // Name of the app whose session is currently active. Set by `establishSession()` when a
  // controller-backed page or client-side event takes ownership of the current flow. Read by
  // `getCurrent()` from places that need the active app without having it passed in — e.g.
  // `resolveActionParams` resolving `source: currentUser`. Single-page runtime, so a global
  // tracker is safe.
  static #currentAppName = null;

  // -----------------------//
  // Public Static Methods //
  // -----------------------//

  static getPkString(pk) {
    return Object.keys(pk).map(pkFieldName => `${pkFieldName}:${pk[pkFieldName]}`).join("-");
  }

  static getPkFromString(pkString) {
    const pk = {};
    pkString.split("-").forEach(pkFieldComponent => {
      const components = pkFieldComponent.split(":");
      pk[components[0]] = components[1];
    });
    return pk;
  }

  static get(appName) {
    if (!App.#instances[appName]) {
      throw new Error("you cannot get an app that hasn't been created yet");
    }
    return App.#instances[appName];
  }

  // Returns the App whose session is currently active. Use this from code that must resolve
  // `source: currentUser` (or other session-scoped data) without being handed an App instance
  // — e.g. `resolveActionParams` running inside a routine. Throws if no session has been
  // established; callers outside the controller/event lifecycle should check state before
  // calling.
  static getCurrent() {
    if (!App.#currentAppName) {
      throw new Error("App.getCurrent() called with no active session — "
        + "call establishSession() on the app (normally the page controller or triggerEvent "
        + "does this) before resolving routine params");
    }
    return App.#instances[App.#currentAppName];
  }

  static samePk(a, b) {
    const pkA = a.pk;
    const pkB = b.pk;
    return compareTwoObjects(pkA, pkB, false);
  }

  // TODO: isHardcodedJson callback is temp
  static async getAppModule(appName) {
    let module;
    try {
      module = await import(`+/${appName}/App.json`);
      module = module.default;
    } catch (e) {
      module = Context.getSource(appName);
    }

    if (!module) {
      throw new Error("This app is not installed yet.");
    }

    return module;
  }

  static async create(appName, releaseId) {
    if (App.#instances[appName]) {
      throw new Error("don't create an app that already exists, get it instead");
    }
    // @todo prevent duplicate creation
    const module = await this.getAppModule(appName);
    const instance = new App(appName, releaseId, module, App.#privateConstructorToken);

    if (!releaseId) {
      await instance.#importPages();
      await instance.#importComponents();
    }
    await instance.#importEntities();
    await instance.#importEvents();

    App.#instances[appName] = instance;
    return instance;
  }

  // ---------------------//
  // Instance Class Vars //
  // ---------------------//

  #appName;
  #releaseId;
  #config;
  #pages = {};
  #components = {};
  #entities = {};
  #events = {};
  #entityStores = {};
  #feedStores = {};
  // Optional per-app config describing the "current user" entity for this app:
  //   { entityType: "Owner", currentUserField: "user" }
  // Parsed from App.json's top-level `currentUser` key. When present, the app's current user
  // is represented by an entity of `entityType`, linked back to User via `currentUserField`.
  #currentUserConfig = null;

  #lastError;
  #ttl = 5000;
  #lastTransactionId = 0;
  #lastTransactionIdUpdatedTimestamp = null;
  #syncInProgress = false;
  #transactionQueue = [];
  #transactionsSent = [];

  // Timer id of the next scheduled `#syncWithServer()` invocation (the
  // 5-second TTL loop). Captured so `flushTransactions()` can cancel
  // the pending tick when it kicks an early sync, avoiding a redundant
  // back-to-back POST when the timer fires moments later.
  #nextSyncTimerId = null;

  // Callbacks waiting for the local transaction queue to be empty AND
  // the in-flight sync (if any) to complete. Populated by
  // `flushTransactions()`; drained at the end of `#syncWithServer()`
  // when both conditions hold. If new transactions queued during the
  // in-flight sync, `#syncWithServer()` re-runs itself before draining
  // the resolvers so callers really do see an empty queue when their
  // promise resolves.
  #flushResolvers = [];

  // Persistent temp-PK → real-PK map populated as the server
  // responds to CreateEntity routines. Used to rewrite queued
  // transactions before they're sent so an event queued against a
  // temp PK after the create's response has arrived (in a
  // subsequent sync batch) still hits the real DB row. Within a
  // single batch the server-side `App::processEvents` does the
  // same rewrite — this map covers the cross-batch case.
  #tempPkToRealPk = new Map();

  // Set of `<type>|<pkString>` strings for every entity we've generated
  // a temp PK for. `#createEntityFromPk` consults this to skip the lazy
  // server fetch when the requested PK is one we just minted — the
  // server doesn't know about temp PKs, so a GET against one would
  // 500. When the temp PK is later swapped to the real PK (server
  // response to the matching CreateEntity), the entry is removed from
  // this set (real-PK fetches go through unchanged).
  #generatedTempPks = new Set();

  // PKs that have been locally deleted via `deleteEntity`. The
  // delete transaction is queued and only flushed on the next
  // sync; in the meantime any reactive consumer still holding the
  // deleted id (the inspector, the layers panel, a still-rendering
  // canvas Vue component) calls `getEntity` for it. Without a
  // tombstone, `getEntity` falls through to `#createEntityFromPk`,
  // which schedules a GET /entities — and the server still has the
  // row, so the response re-upserts it via `mergeResponsePayload`
  // and the canvas/feed pop the element back in visually until the
  // user refreshes. Tombstones short-circuit that path so a local
  // delete is final from the client's POV. Entries are kept for
  // the lifetime of the App instance (page session) — pks aren't
  // reused, and the set is just `${type}|${pkString}` strings.
  #tombstones = new Set();

  // Set to `true` for the duration of a `mergeResponsePayload` call.
  // `Entity.#requestInitFeeds` checks this and skips its
  // `triggerInitialLoad` cascade — the server has already preloaded
  // every initQty != 0 feed for the entities in this response (via
  // `expandInitQtyFeeds`), so re-triggering on the client just
  // queues redundant fetches that hit the next polling tick as a
  // multi-MB /feeds POST that returns 0 bytes per request. Defaults
  // to `false` so paths outside of a response merge (lazy fetch
  // landing, optimistic create) still get the auto-load behaviour
  // their callers expect.
  #isMergingResponse = false;

  // -------------------------//
  // Public Instance Methods //
  // -------------------------//

  constructor(appName, releaseId, config, token) {
    super();
    if (token !== App.#privateConstructorToken) {
      throw new Error("Use App.create(); constructor is not public.");
    }
    this.#appName = appName;
    this.#releaseId = releaseId;
    this.#config = config;
    this.#currentUserConfig = config?.currentUser ?? null;
    this.establishSession();
    this.#syncWithServer();
  }

  // Declares this app as the active session. Invoked from the constructor (so `App.create()` /
  // `App.get()` users get a session by default) and again from `triggerEvent()` / entity
  // actions right before they fire routines, so `source: currentUser` always resolves against
  // the correct app when multiple apps coexist.
  establishSession() {
    App.#currentAppName = this.#appName;
  }

  // Returns the PK of the current user's app-scoped user entity (e.g. the Owner entity in
  // Studio), used to resolve `source: currentUser` in routine params.
  //
  // The PK is sourced from the page's injected `contexts.currentUser` — the controller
  // resolved it from auth server-side and handed it to the page via window.blclient.data.
  // That single injection point is the client's "session": it was produced by trusted code,
  // it does not depend on any caller-supplied context map, and it is consistent with what
  // the server will authoritatively compute when the event is replayed.
  //
  // For apps without a `currentUser` config (legacy `userEntity: "User"` apps) we fall back
  // to `{ id: <userId> }`, pulling the user id from the well-known window data.
  getCurrentUserEntityPk() {
    const injected = window?.blclient?.data?.pageProps?.contexts?.currentUser?.pk;
    if (injected) {
      return injected;
    }
    const userId = window?.blclient?.data?.user?.userId
      ?? window?.blclient?.data?.userId
      ?? window?.blclient?.data?.inspirationUserId;
    if (userId === undefined || userId === null) {
      throw new Error("getCurrentUserEntityPk(): no current-user context injected on "
        + "window.blclient.data.pageProps.contexts.currentUser and no fallback user id "
        + "on window.blclient.data — this app expects a logged-in session established "
        + "by the page controller");
    }
    return { id: userId };
  }

  // Resolves when every currently-queued transaction has been POSTed and
  // its response processed (temp→real PK swap applied). Used by the
  // paste flow to keep the "Pasting elements..." modal up until the
  // server has acknowledged every CreateEntity event — otherwise a fast
  // user can navigate away from the page while temp PKs are still in
  // the queue, and on return the local cache shows stale temp-PK
  // entities until the next 5-second TTL tick.
  //
  // Behaviour:
  //   - If queue is empty and no sync is in flight, resolves on the next
  //     microtask.
  //   - If a sync is in flight, waits for it. If new transactions
  //     queued during the sync, `#syncWithServer`'s finally loops back
  //     into another sync before resolving — so the promise really
  //     means "queue is drained", not just "one round-trip done".
  //   - If no sync is in flight but the queue is non-empty, cancels the
  //     pending TTL timer and kicks an immediate sync.
  flushTransactions() {
    return new Promise(resolve => {
      if (this.#transactionQueue.length === 0 && !this.#syncInProgress) {
        Promise.resolve().then(resolve);
        return;
      }
      this.#flushResolvers.push(resolve);
      if (!this.#syncInProgress) {
        if (this.#nextSyncTimerId !== null) {
          clearTimeout(this.#nextSyncTimerId);
          this.#nextSyncTimerId = null;
        }
        this.#syncWithServer();
      }
    });
  }

  // Force a sync round-trip regardless of whether the local queue is
  // empty. `flushTransactions` short-circuits when there's nothing to
  // push, which is the right semantics for "wait for my writes to
  // land" but the wrong semantics when something OUT-OF-BAND has
  // mutated server state (an AI agent firing triggerEvent server-side,
  // a collaborator's edit landing on the server). Use this to pull
  // those changes into the local store without waiting for the next
  // 5s polling tick.
  pullServerTransactions() {
    // eslint-disable-next-line no-console
    console.log("[pullServerTransactions] called", {
      lastTransactionId: this.#lastTransactionId,
      syncInProgress: this.#syncInProgress,
      queueLength: this.#transactionQueue.length,
    });
    return new Promise(resolve => {
      this.#flushResolvers.push(resolve);
      if (this.#syncInProgress) {
        return;
      }
      if (this.#nextSyncTimerId !== null) {
        clearTimeout(this.#nextSyncTimerId);
        this.#nextSyncTimerId = null;
      }
      this.#syncWithServer();
    });
  }

  updateEntityFeedLastSeenIndicator(entityType, entityPk, fieldName) {
    const store = this.getFeedStore(entityType, entityPk, fieldName);
    store.updateLastSeenIndicator(store.latestPkString);
  }

  updateEntityFeedLastSeen(entityType, entityPk, fieldName) {
    const store = this.getFeedStore(entityType, entityPk, fieldName);
    store.updateLastSeenPkString(store.latestPkString);
  }

  setFeedViewStatus(entityType, entityPk, fieldName, status) {
    const store = this.getFeedStore(entityType, entityPk, fieldName);
    store.setViewStatus(status);
  }

  secondsSinceLastTransactionUpdateGreater() {
    return Math.floor((Date.now() - this.#lastTransactionIdUpdatedTimestamp) / 1000) > Math.floor(this.#ttl * 1.5 / 1000);
  }

  getComponent(componentName) {
    return this.#components[componentName];
  }

  getPage(pageName) {
    return this.#pages[pageName];
  }

  getAppEvents() {
    return this.#events;
  }

  get releaseId() {
    return this.#releaseId;
  }

  assertArrayOfEntitiesHavingValidType(items, entityType) {
    this.#assertKnownEntityType(entityType);
    // TODO: in foreach:items is an array of PK objects -> validate later that they exist
    // DataTypes.assertArrayOf(items, Entity);
    // items.forEach((item, index) => {
    //   if (item.getType() !== entityType) {
    //     throw new Error(`expected every item to be an Entity of type "${entityType}", but item at index ${index} is of type "${item.getType()}"`);
    //   }
    // });
  }

  // @TODO: if these public methods are required remove private ones
  getEntityFieldDef(entityType, fieldName) {
    return this.#getFieldDef(entityType, fieldName);
  }

  getEntityPrimaryKeyFields(entityType) {
  // const pkFields = this.#config.entities[type].primaryKeyFields;
    const pkFields = this.#entities[entityType].primaryKeyFields;
    return pkFields;
  }

  createTempEntity(type, fields) {
    // console.log(`ENTITY - creating temp entity type=${type} fields=${JSON.stringify(fields)}`);
    if (!this.#entities[type]) {
      throw new Error(`cannot create temp entity of type ${type} because it was not loaded properly`, this.#entities);
    }

    const entityConfig = this.#entities[type];
    const tempEntity = Entity.makeTemp(this.#appName, entityConfig, fields);
    const store = this.#getEntityStore(type);
    const pk = tempEntity.getPk();
    const pkString = App.getPkString(pk);
    // Track the temp PK so `#createEntityFromPk` knows to skip the
    // server fetch for it. The server doesn't recognise temp PKs;
    // fetching one returns 500 and pollutes the console with errors.
    this.#generatedTempPks.add(`${type}|${pkString}`);
    store.storeEntity(pkString, tempEntity, false, "create temp entity");

    // Current-user PK is pulled from the active session, not from a caller-supplied context.
    // See getCurrentUserEntityPk(): the session is the only trusted source for "who am I".
    this.#updateRelatedEntities(tempEntity, { pk: this.getCurrentUserEntityPk() });

    // recurse to ensure we return the entity stored in pinia
    return this.getEntity(type, pk);
  }

  getEntityField(entityType, entityPk, fieldName) {
    if (!entityPk || !entityType || !fieldName) {
      throw new BaseFrameworkError("get entity field must define all required params", {
        entityType: entityType ?? "<not defined",
        entityPk: entityPk ?? "<not defined",
        fieldName: fieldName ?? "<not defined",
      });
    }

    if (fieldName.includes(".")) {
      const [leftmostSegment, ...rest] = fieldName.split(".");
      const remainingPath = rest.join(".");

      const fieldDef = this.getEntityFieldDef(entityType, leftmostSegment);

      if (fieldDef.type !== "oneEntity") {
        throw new Error("dot notation only works on oneEntity fields");
      }

      const nestedEntity = this.getEntityField(entityType, entityPk, leftmostSegment);

      if (!nestedEntity) {
        return null;
      }

      return this.getEntityField(nestedEntity.entityType, nestedEntity.pk, remainingPath);
    }

    const store = this.#getEntityStore(entityType);
    const pkString = App.getPkString(entityPk);
    if (!store.hasEntity(pkString)) {
      this.#createEntityFromPk(entityType, entityPk);
    }
    return store.getEntityField(pkString, fieldName);
  }

  getEntityConfig(type) {
  // return this.#config.entities[type];
    return this.#entities[type];
  }

  hasEntity(type, pk) {
    const store = this.#getEntityStore(type);
    return store.hasEntity(App.getPkString(pk));
  }

  getEntity(type, pk) {
    pk = this.#normalizePk(type, pk);
    // Redirect through the temp→real PK mapping: a paste creates
    // entities under temp PKs that the server then swaps to real PKs.
    // Older code paths (Vue render trees, scheduled fetches) can still
    // hold the original temp PK references; routing them through the
    // map ensures `getEntity(type, tempPk)` returns the real entity
    // instead of triggering a `#createEntityFromPk` lazy fetch with
    // a temp PK that the server can't resolve.
    pk = this.#rewriteTempPksInTransaction(pk);
    // console.log(`ENTITY - getEntity searching for type=${type} pk=${JSON.stringify(pk)}`);
    const store = this.#getEntityStore(type);
    const pkString = App.getPkString(pk);

    // Locally deleted: skip the lazy create+fetch. Returning undefined
    // here is what stops the canvas/inspector from racing back to the
    // server for an entity we just removed.
    if (this.#tombstones.has(this.#tombstoneKeyFor(type, pkString))) {
      return undefined;
    }

    if (!store.hasEntity(pkString)) {
      // console.log(`ENTITY - hasEntity did not find type=${type} pk=${JSON.stringify(pk)}`);
      this.#createEntityFromPk(type, pk);
    }

    const entity = store.getEntity(pkString);
    // console.log(`ENTITY - getEntity found type=${type} pk=${JSON.stringify(pk)}`);
    return entity;
  }

  // Stable key for the tombstone set. `${type}|${pkString}` is unique
  // across entity types (a Story id 5 and an Element id 5 don't collide).
  #tombstoneKeyFor(type, pkString) {
    return `${type}|${pkString}`;
  }

  isTombstoned(type, pk) {
    return this.#tombstones.has(this.#tombstoneKeyFor(type, App.getPkString(pk)));
  }

  // Read by `Entity.#requestInitFeeds` (via `App.get(...).isMergingResponse()`)
  // to skip the initQty auto-trigger when the entity arrived as part of a
  // server-response merge. See `#isMergingResponse` for why.
  isMergingResponse() {
    return this.#isMergingResponse;
  }

  /**
   * Normalizes callers' pk inputs into a clean `{pkField: value}` map.
   *
   * Handles three shapes:
   *  - Entity instance  → use entity.getPk() (avoids iterating every enumerable field)
   *  - Plain PK object  → returned as-is (after filtering)
   *  - Dirty PK object  → something like { id, indexInParent, parentElement, ... } as passed
   *                       via event params; filter to only the type's declared PK fields
   *
   * Without this, `App.getPkString` enumerates ALL enumerable keys (Entity fields include
   * every data field) or extra keys on a dirty object, producing a garbage pkString that
   * misses the entity store, falls through to `#fetchAndStoreEntity`, and axios serializes
   * the Entity instance as "[object Object]" in the query string → server 500.
   */
  #normalizePk(type, pk) {
    if (pk && typeof pk.getPk === "function") {
      return pk.getPk();
    }
    if (pk === null || pk === undefined || typeof pk !== "object") {
      throw new Error(`App.getEntity: pk must be an Entity instance or an object, got ${typeof pk}`);
    }
    const pkFields = this.getEntityPrimaryKeyFields(type);
    const clean = {};
    pkFields.forEach(field => {
      clean[field] = pk[field];
    });
    // Guard: a pk with every field undefined will serialize to {} in the query
    // string, the server sees no `pk` key at all, and routeGetEntities 500s on
    // `Undefined array key "pk"`. Throw locally so the bad caller names itself
    // in the stack.
    const hasAnyValue = pkFields.some(field => clean[field] !== undefined && clean[field] !== null && clean[field] !== "");
    if (!hasAnyValue) {
      throw new Error(`App.getEntity: pk for type "${type}" has no values on fields [${pkFields.join(", ")}]; caller passed ${JSON.stringify(pk)}`);
    }
    return clean;
  }

  getAllEntitiesOfType(entityType) {
    return this.#getEntityStore(entityType).getAllEntities();
  }

  updateSpecificEntityFields(entityType, entityPk, fieldsToUpdate) {
    const store = this.#getEntityStore(entityType);
    store.updateEntity(App.getPkString(entityPk), fieldsToUpdate);
  }

  upsertEntity(type, pk, fieldValues, skipStateOnlyFields, shouldUpdateLastSeenIndicator = true, reason) {
    const store = this.#getEntityStore(type);
    const pkString = App.getPkString(pk);

    // Server response carrying a row we just locally deleted — typically a
    // race where a concurrent /entities or /feeds query returns the still-
    // present row before our queued delete transaction has been flushed.
    // Re-storing it would re-splice into every parent feed and the canvas
    // pops the element back in. Drop it on the floor; the next refresh
    // (or the eventual delete-transaction round-trip) will agree we're
    // done with this pk.
    if (this.#tombstones.has(this.#tombstoneKeyFor(type, pkString))) {
      return;
    }

    // Capture old feeds BEFORE mutating the entity so we can detect parent changes
    const oldFeeds = store.hasEntity(pkString)
      ? this.getAllFeedsThatRequireEntityToBeInserted(type, pk)
      : {};

    const entity = store.hasEntity(pkString) ? store.getEntity(pkString)
      : new Entity(this.#appName, this.#entities[type], pk);

    entity.hydrateFields(fieldValues, skipStateOnlyFields);
    entity.init(this);
    store.upsertEntity(pkString, entity, reason);

    // Remove from all old feeds before re-splicing. This handles both parent changes (feed ID
    // changes → entity leaves old parent's feed) and same-parent reordering (indexInParent
    // changes → entity must be re-spliced at its new position in the same feed).
    Object.values(oldFeeds).forEach(feed => {
      feed.removeItemFromFeed(pkString);
    });

    this.#upsertIntoRelevantFeeds(type, pk, entity, shouldUpdateLastSeenIndicator);
    this.updateAllSyntheticReciprocalLinks(type, pk, entity);
  }

  // TODO: Nick - wants to compare this to #updateRelatedEntities
  updateAllSyntheticReciprocalLinks(entityType, entityPk, fieldValues) {
    const oneEntityFieldDefinitions = this.#getFieldsDefinitionsOfTypeOneEntityForEntity(entityType);
    Object.entries(oneEntityFieldDefinitions).forEach(([oneEntityFieldKey, oneEntityFieldDefinition]) => {
      if (!this.hasEntity(oneEntityFieldDefinition["entity"], fieldValues[oneEntityFieldKey])) {
        return;
      }

      const reciprocalDefs = this.#getFieldsDefinitionsOfTypeOneEntityForEntity(oneEntityFieldDefinition["entity"]);
      Object.entries(reciprocalDefs).forEach(([reciprocalKey, reciprocalDef]) => {
        if (reciprocalDef.entity === entityType) {
          this.updateSpecificEntityFields(
            oneEntityFieldDefinition["entity"],
            fieldValues[oneEntityFieldKey],
            { [reciprocalKey]: entityPk },
          );
        }
      });
    });

  // grab fieldValues of type oneEntity
  // loop over and see if we have those entities in memory
  // if we do AND they contain syntheticReciprocalLinks
  // THEN update them to MY_PK
  }

  getAllFeedsThatRequireEntityToBeInserted(entityType, entityPk) {
    const feedStoresNeedingEntityInserted = {};

    const oneEntityFields = this.#getFieldsDefinitionsOfTypeOneEntityForEntity(entityType);
    const entity = this.getEntity(entityType, entityPk);

    Object.entries(oneEntityFields).forEach(([entityField, entityFieldDefinition]) => {

      const correspondingEntityFieldDefinition = this.#getEntityDef(entityFieldDefinition.entity);
      const correspondingEntityFieldDefinitionFields = correspondingEntityFieldDefinition.fields;
      const correspondingMatchingFields = Object.entries(correspondingEntityFieldDefinitionFields).filter(entry => {
        const fieldDefinition = entry[1];
        return fieldDefinition.entity === entityType && fieldDefinition.type === "manyEntities";
      });

      if (correspondingMatchingFields.length === 0) {
        return;
      }

      correspondingMatchingFields.forEach(correspondingMatchingField => {
        const correspondingMatchingFieldName = correspondingMatchingField[0];
        const correspondingMatchingFieldDef = correspondingMatchingField[1];
        const related = entity[entityField];

        // TODO: confirm if this is the correct solution - fails for self referencing entities (message -> message)
        if (!related || !related.pk) {
          return;
        }

        // Honour `additionalLinkingCriteria` — a `manyEntities` field
        // filtered by a fixed field value (e.g. `Destination.pages` only
        // wants DCs with `type: "page"`, `Destination.components` only
        // wants `type: "component"`). Without this check, every
        // newly-created DC would land in every type-filtered feed on the
        // destination, polluting both sidebars.
        if (!this.#entityMatchesAdditionalLinkingCriteria(entity, correspondingMatchingFieldDef.additionalLinkingCriteria)) {
          return;
        }

        const feedId = FeedStore.getId(
          this.#appName,
          entityFieldDefinition.entity,
          App.getPkString(entity[entityField].pk),
          correspondingMatchingFieldName,
        );

        // Lazily create the feed if it doesn't exist yet. Previously this
        // only returned feeds that were already instantiated — but when a
        // brand-new parent entity is created optimistically (e.g. the
        // wrapper a `WrapElements` event spawns) and its children's
        // `parentElement` is then re-pointed to that new parent via
        // `MoveElement`, the new parent's children-feed store hadn't been
        // touched yet, so the children silently dropped off their old
        // parent's feed without being added to the new one. They stayed
        // invisible until the server round-tripped and a first read
        // lazily created the feed. Creating it here makes the client-side
        // splice land immediately.
        if (!this.#feedStores[feedId]) {
          this.getFeedStore(
            entityFieldDefinition.entity,
            entity[entityField].pk,
            correspondingMatchingFieldName,
          );
        }
        if (this.#feedStores[feedId]) {
          feedStoresNeedingEntityInserted[feedId] = this.#feedStores[feedId];
        }
      });
    });

    return feedStoresNeedingEntityInserted;
  }

  // Returns true when `entity` satisfies every constraint in
  // `additionalLinkingCriteria`. Supports the only source currently
  // needed in App.json: `constant` — compare the entity's field value
  // against the constant. An absent `additionalLinkingCriteria` trivially
  // matches. Unknown sources are treated as a match rather than failing
  // closed, so adding a new criteria source later doesn't silently
  // exclude optimistically-created entities until both sites are updated.
  #entityMatchesAdditionalLinkingCriteria(entity, criteria) {
    if (!criteria) {
      return true;
    }
    const fieldData = entity.getFieldData();
    return Object.entries(criteria).every(([fieldName, constraint]) => {
      if (constraint?.source !== "constant") {
        return true;
      }
      return fieldData[fieldName] === constraint.value;
    });
  }

  getFeedItems(entityType, entityPk, fieldName) {
    const store = this.getFeedStore(entityType, entityPk, fieldName);
    return store.items ?? [];
  }

  getFeedMeta(entityType, entityPk, fieldName, metaKey) {
    const store = this.getFeedStore(entityType, entityPk, fieldName);
    return store[metaKey];
  }

  loadMoreNext(entityType, entityPk, fieldName) {
    const store = this.getFeedStore(entityType, entityPk, fieldName);
    return store.loadMoreNext();
  }

  loadMorePrevious(entityType, entityPk, fieldName) {
    const store = this.getFeedStore(entityType, entityPk, fieldName);
    return store.loadMorePrevious();
  }

  /*
getFeedDefinitionFromFeedId(id) {
  const feedParts = Feed.getPartsFromId(id);
  const parentEntityPrimaryKey = feedParts.pk;
  const parentEntity = this.getEntity(feedParts.entityType, parentEntityPrimaryKey);
  const feedInstance = parentEntity.fields[feedParts.fieldName];
  return feedInstance.fieldDefinition;
}

getFeedIsLoading(entityType, entityPk, fieldName) {
  const store = this.#getFeedStore(entityType, entityPk, fieldName);
  const isLoading = store.getIsLoading();
  return isLoading;
}

getFeedMetadata(entityType, entityPk, fieldName) {
  const store = this.#getFeedStore(entityType, entityPk, fieldName);
  const metadata = store.getMetadata();
  return metadata;
}

fetchAndStoreFeed(entityType, entityPk, fieldName) {
  const feedStore = this.#getFeedStore(entityType, entityPk, fieldName);
  if (!feedStore.getMetadata().hasMore) {
    throw new Error("Should not fetch feed when hasMore = false", feed.getId());
  }
  if (feedStore.isLoadingNext) {
    throw new Error("Should not fetch feed when loading = true", feed.getId());
  }
  feedStore.setIsLoadingNext(true);
  const requestOffsets = feedStore.getOffsets();
  // POST rather than GET — see the matching comment in
  // `FeedStore#paginateFeed` for why (request header / query string
  // length limit). Both feed fetch paths must stay in sync.
  this.apiPost(
    `/app/${this.#appName}/feeds`,
    {
      feeds: [
        {
          entity_type: feed.entityType,
          entity_pk: feed.pk,
          field_name: feed.fieldName,
          offsets: requestOffsets,
        }
      ]
    },
    response => {
      const entities = response.entities;
      Object.entries(entities).forEach(([entityType, entitiesOfType]) => {
        Object.entries(entitiesOfType).forEach(([entityPkString, entityOfType]) => {
          const entityPk = JSON.parse(entityPkString);
          this.#storeEntity(
            entityType,
            entityPk,
            entityOfType,
          );
        });
      });
      feedStore.storeFeedResponse(response, requestOffsets);
    },
    () => {
    },
    () => {
      feedStore.setIsLoadingNext(false);
    },
  );
}
*/

  // ----------- //
  // Event Stuff //
  // ----------- //

  deleteEntity(type, pk) {
    const pkString = App.getPkString(pk);

    // Resolve the parent-feed set BEFORE adding the tombstone — the
    // resolver reads the entity's `parentElement` (etc.) via getEntity,
    // which short-circuits on tombstones. After the feeds are known we
    // tombstone, then mutate. Once tombstoned, any subsequent getEntity
    // for this pk returns undefined and any incoming upsertEntity is
    // dropped — closing the race that would otherwise let a lazy fetch
    // (kicked off by a still-rendering reactive consumer) re-introduce
    // the row from the server before the queued delete transaction has
    // been flushed.
    const feeds = this.getAllFeedsThatRequireEntityToBeInserted(type, pk);
    this.#tombstones.add(this.#tombstoneKeyFor(type, pkString));
    Object.values(feeds).forEach(feed => {
      feed.removeItemFromFeed(pkString);
    });

    const store = this.#getEntityStore(type);
    if (store.hasEntity(pkString)) {
      store.removeEntity(pkString);
    }
  }

  getFeedStore(entityType, entityPk, fieldName) {
    if (!entityType || !entityPk || !fieldName) {
      return {};
    }

    if (fieldName.includes(".")) {
      const parts = fieldName.split(".");
      let currentType = entityType;
      let currentPk = entityPk;

      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        const isLast = i === parts.length - 1;

        if (isLast) {
          return this.getFeedStore(currentType, currentPk, part);
        }

        const fieldDef = this.getEntityFieldDef(currentType, part);
        const value = this.getEntityField(currentType, currentPk, part);

        if (value && fieldDef && fieldDef.type === "oneEntity") {
          currentType = fieldDef.entity;
          currentPk = null;
          if (value && value.pk) {
            currentPk = value.pk;
          } else if (value && value.id) {
            currentPk = value;
          }
        } else {
          return {};
        }
      }
    }

    const id = FeedStore.getId(this.#appName, entityType, App.getPkString(entityPk), fieldName);
    if (!this.#feedStores[id]) {
      const fieldDef = this.#getFieldDef(entityType, fieldName);
      this.#feedStores[id] = new FeedStore(this.#appName, entityType, entityPk, fieldName, fieldDef);
    }
    return this.#feedStores[id];
  }

  /**
 * Fire an event and queue it as a transaction to be sent to the server.
 * This is the correct entry point for events triggered from UI components and entity actions.
 *
 * IMPORTANT — events receive only `eventParams`. The page/action contexts map is NOT passed in
 * and NOT available inside the event's routines. Callers that hold a contexts map (e.g. entity
 * actions) MUST resolve every value they want the event to see into `eventParams` at the action
 * boundary and then drop the contexts reference. Routines can reach session-scoped state (the
 * current user) via `source: currentUser`, which reads from the active session — never from
 * contexts or event params.
 *
 * Do NOT call this from inside a routine or any function a routine calls. Routines execute
 * inside an already-queued transaction; calling triggerEvent() from within one would queue the
 * sub-event as a separate transaction and send it to the server independently, causing
 * duplicate or out-of-order processing. Use triggerEventWithoutSendingToServer() instead.
 */
  triggerEvent(eventType, eventParams) {
    this.establishSession();
    const { event, params } = this.#executeEvent(eventType, eventParams);
    this.#transactionQueue.push(event.getTransactionObject());
    // Return the resolved param context (including routine outputs)
    // so callers can chain follow-up events that depend on the new
    // entity's id — e.g. the variant-create flow reads the new
    // CreateDynamicComponent + createRootElement ids and threads
    // them into AddElement calls. Mirrors the PHP triggerEvent
    // return contract so client + server code shape matches.
    return params;
  }

  /**
 * Fire an event without queuing a transaction. Use this from inside routines and any functions
 * that routines call (e.g. Foreach sub-events). The parent event's transaction already captures
 * the full operation for the server; sub-events only need their client-side effects applied.
 *
 * Same contract as triggerEvent(): event params are the only data the event sees. No contexts.
 *
 * Returns the fully resolved param context after all routines have run, so callers can read
 * routine outputs if needed.
 */
  triggerEventWithoutSendingToServer(eventType, eventParams) {
    this.establishSession();
    const { params } = this.#executeEvent(eventType, eventParams);
    return params;
  }

  /**
   * Merges the top-level `entities` and `feeds` blocks of any server response (from
   * /feeds, /entities, or /transactions) into local stores. Safe to call when either
   * key is missing — absent blocks are simply skipped.
   *
   * Processing order is feeds-first-then-entities. The reason is `Entity.init` (called
   * from `upsertEntity`) auto-triggers `triggerInitialLoad` on every `manyEntities`
   * field with a non-zero `initQty`. If entities are upserted first, those auto-loads
   * see an empty FeedStore and queue a fetch — even though the same response carries
   * the preloaded feed page from the server's `expandInitQtyFeeds`. With 19 destinations
   * × 4 feeds + every DC × 1 + every Element × 1 in scope, that fan-out flushes on
   * the next polling tick as one giant /feeds POST (1.5 MB+) and a multi-second main-
   * thread freeze. Integrating feeds first means `triggerInitialLoad`'s
   * `orderedKeys.length > 0` guard short-circuits and no redundant fetch is queued.
   *
   * Vue reactivity is batched, so any v-for re-render that depends on both feed items
   * and entity bodies runs after this whole method returns — by which time both halves
   * of the response are present and no rendering-time lazy fetch fires.
   *
   * `shouldUpdateLastSeenIndicator` controls whether upserting an entity into any
   * feed it already belongs to also advances the feed's last-seen indicator. The
   * /feeds path passes `false` because the entities arrived via a feed query and
   * auto-advancing on them would be self-referential. /entities and /transactions
   * default to `true` — those entities weren't delivered through a feed query so
   * updating last-seen when they happen to land in a visible feed is correct.
   */
  mergeResponsePayload(uri, response, { shouldUpdateLastSeenIndicator = true } = {}) {
    // Set the merge flag so `Entity.#requestInitFeeds` skips its
    // auto-trigger cascade. Wrap in try/finally so a throw inside
    // the merge doesn't strand the flag in `true` and silently
    // suppress every subsequent legitimate auto-load.
    this.#isMergingResponse = true;
    try {
      this.#mergeResponsePayloadInner(uri, response, { shouldUpdateLastSeenIndicator });
    } finally {
      this.#isMergingResponse = false;
    }
  }

  #mergeResponsePayloadInner(uri, response, { shouldUpdateLastSeenIndicator }) {
    if (response?.feeds) {
      Object.keys(response.feeds).forEach(entityType => {
        Object.keys(response.feeds[entityType]).forEach(entityPkString => {
          const entityPk = App.getPkFromString(entityPkString);
          Object.keys(response.feeds[entityType][entityPkString]).forEach(fieldName => {
            const store = this.getFeedStore(entityType, entityPk, fieldName);
            Object.keys(response.feeds[entityType][entityPkString][fieldName]).forEach(direction => {
              response.feeds[entityType][entityPkString][fieldName][direction].forEach(feedResponse => {
                // Strip tombstoned items from the feed page before
                // integration. The matching entity upsert is dropped
                // below (or in #storeEntity), so without this filter
                // the feed would carry an orphan reference and the
                // canvas's v-for would still render a slot for the
                // deleted pk while its DynamicElement.element resolved
                // to undefined.
                const filteredFeedResponse = this.#filterTombstonedFeedItems(feedResponse);
                store.integrateFeedResponse(direction, filteredFeedResponse);
              });
            });
          });
        });
      });
    }
    if (response?.entities) {
      Object.keys(response.entities).forEach(entityType => {
        Object.keys(response.entities[entityType]).forEach(pkJsonString => {
          const pk = JSON.parse(pkJsonString);
          this.upsertEntity(
            entityType,
            pk,
            response.entities[entityType][pkJsonString],
            false,
            shouldUpdateLastSeenIndicator,
            `merge response payload - ${uri}`,
          );
        });
      });
    }
  }

  // Returns a feedResponse with any item whose `(entityType, entityPk)` is
  // tombstoned removed. Pure — never mutates the input.
  #filterTombstonedFeedItems(feedResponse) {
    const items = feedResponse?.feed;
    if (!Array.isArray(items) || items.length === 0) {
      return feedResponse;
    }
    const filtered = items.filter(item => {
      const itemPkString = App.getPkString(item.entityPk);
      return !this.#tombstones.has(this.#tombstoneKeyFor(item.entityType, itemPkString));
    });
    if (filtered.length === items.length) {
      return feedResponse;
    }
    return { ...feedResponse, feed: filtered };
  }

  // --------------------------//
  // Private Instance Methods //
  // --------------------------//

  #executeEvent(eventType, eventParams) {
    const newEvent = new Event(this.#appName, eventType, this.#events[eventType]);
    const resultParams = newEvent.executeRoutines(eventParams);
    return { event: newEvent, params: resultParams };
  }// can/should we remove this in favor of exclusively calling upsert?

  #storeEntity(type, pk, fieldValues, skipStateOnlyFields, reason) {
    if (Object.prototype.toString.call(pk) !== "[object Object]" || pk instanceof String) {
      const pkType = typeof pk;
      throw new Error(`PK should be typeof Object. ${pkType} given`);
    }

    const store = this.#getEntityStore(type);

    const pkString = App.getPkString(pk);

    // Locally deleted: drop server-broadcast updates (transactions output)
    // for this pk so they don't re-store the entity and re-splice it into
    // the parent feed. Same race as the upsertEntity guard, different
    // ingestion path.
    if (this.#tombstones.has(this.#tombstoneKeyFor(type, pkString))) {
      return;
    }

    const existingEntityFields = store.getEntityFieldData(pkString);

    const entity = store.hasEntity(pkString) ? store.getEntity(pkString)
      : new Entity(this.#appName, this.#entities[type], pk);

    entity.hydrateFields({ ...existingEntityFields, ...fieldValues }, skipStateOnlyFields);
    entity.init();
    // @TODO: can we just call app.upsertEntity instead so that there is a unified flow and we restrict store.upsertEntity so that it can only be called from app.upsertEntity
    store.upsertEntity(App.getPkString(pk), entity, reason);

    this.#upsertIntoRelevantFeeds(type, pk, entity);

    const tempPkString = entity.getTempPkString();
    if (store.hasEntity(tempPkString)) {
      store.removeEntity(tempPkString);
    }
    // Keep the `#generatedTempPks` entry — old UI components (Vue
    // render trees, scheduled fetches) may still hold references to
    // the temp PK after the swap. If anything tries to fetch the temp
    // PK later, we still want the "skip server fetch" guard to fire.
    // (The temp→real rewrite at getEntity/createEntityFromPk handles
    // the read side; this guards the lazy-fetch side.)
    // Record the temp→real PK mapping so any transactions queued
    // against the temp PK get rewritten before they leave the
    // browser. The server-side `processEvents` rewrite covers
    // within-batch reconciliation; this covers the cross-batch
    // case where an UpdateEntity is queued AFTER the create's
    // response has returned but BEFORE the JS state has caught
    // up at the event-fire site.
    this.#registerTempPkMapping(entity, pk);
  }

  // Register every (tempPkField → realPkField) pair from this
  // entity's PK pair into the persistent rewrite map. Keys are
  // string-coerced so number/string mismatches in queued params
  // don't cause misses.
  #registerTempPkMapping(entity, realPk) {
    const tempPk = entity.getTempPk?.() ?? {};
    Object.keys(tempPk).forEach(field => {
      const tempValue = tempPk[field];
      const realValue = realPk?.[field];
      if (tempValue === undefined || realValue === undefined) {
        return;
      }
      if (String(tempValue) === String(realValue)) {
        return;
      }
      this.#tempPkToRealPk.set(String(tempValue), realValue);
    });
  }

  // Walk a transaction's eventParams payload and rewrite any
  // scalar value matching a known temp PK to the real PK. The
  // App-engine schema lets entity-PK references nest arbitrarily
  // deep (event params → routine fields → oneEntity refs), so
  // the walk recurses through every plain object / array.
  #rewriteTempPksInTransaction(transaction) {
    if (this.#tempPkToRealPk.size === 0) {
      return transaction;
    }
    const map = this.#tempPkToRealPk;
    const rewrite = node => {
      if (Array.isArray(node)) {
        return node.map(rewrite);
      }
      if (node !== null && typeof node === "object") {
        const result = {};
        Object.keys(node).forEach(key => {
          result[key] = rewrite(node[key]);
        });
        return result;
      }
      if ((typeof node === "string" || typeof node === "number") && map.has(String(node))) {
        return map.get(String(node));
      }
      return node;
    };
    return rewrite(transaction);
  }

  #upsertIntoRelevantFeeds(type, pk, entity, shouldUpdateLastSeenIndicator = true) {
    const feeds = this.getAllFeedsThatRequireEntityToBeInserted(type, pk);
    Object.values(feeds).forEach(feed => {
    // TODO: this might need adjustments - might need to be upsertIntoFeed
      feed.spliceItemIntoFeed(entity, pk);
      this.#updateFeedLastSeenIndicator(entity, feed, pk, shouldUpdateLastSeenIndicator);
    });
  }

  #updateFeedLastSeenIndicator(entity, feed, pk, shouldUpdateLastSeenIndicator) {
    if (this.#shouldUpdateFromTempPkToActualPk(entity, feed)) {
      this.#handleFeedLastSeenIndicatorUpdate(App.getPkString(entity.getPk()), feed, entity);
      return;
    }

    const newPkString = `${entity.getType()}-${App.getPkString(pk)}`;
    const indexOfNewKey = feed.orderedKeys.indexOf(newPkString);
    const isKeyAfterLastSeen = feed.lastSeenItemIndex < indexOfNewKey;
    const isFeedActivelyBeingViewed = feed.feedIsBeingViewed;

    if (isFeedActivelyBeingViewed && shouldUpdateLastSeenIndicator && isKeyAfterLastSeen) {
      this.#handleFeedLastSeenIndicatorUpdate(App.getPkString(pk), feed, entity, indexOfNewKey);
    }
  }

  #shouldUpdateFromTempPkToActualPk(entity, feed) {
    const store = this.#getEntityStore(entity.getType());
    const storeHasTempEntity = store.hasEntity(entity.getTempPkString());
    return storeHasTempEntity && this.#isCurrentFeedLastReadOfTypeTempEntity(feed, entity);
  }

  #isCurrentFeedLastReadOfTypeTempEntity(feed, entity) {
    const existingFeedLastReadPkString = feed.lastSeenIndicatorItemPkString;
    const entityTempPkString = entity.getTempPkString();
    return existingFeedLastReadPkString === entityTempPkString;
  }

  #handleFeedLastSeenIndicatorUpdate(pkString, feed, entity, newLastSeenItemIndex = null) {
    if (newLastSeenItemIndex) {
      feed.lastSeenItemIndex = newLastSeenItemIndex;
    }
    feed.updateLastSeenIndicator(pkString, entity.isEntityPkTemp());
  }

  #getFieldsDefinitionsOfTypeOneEntityForEntity(entityType) {
    const entityDefinition = this.#getEntityDef(entityType);
    const entityFields = entityDefinition.fields;
    return Object.fromEntries(
      Object.entries(entityFields)
          .filter(entry => entry[1].type === "oneEntity" && !entry[1].stateOnly && entry[1].entity !== "User"),
    );
  }

  #createEntityFromPk(type, pk) {
    if (!this.#entities[type]) {
      throw new Error(`cannot create entity of type ${type} because it was not loaded properly`, this.#entities);
    }
    // Defensive temp→real redirect — `getEntity` already rewrites at
    // the entry, but `getEntityField` and direct callers also land
    // here. Doing the rewrite once at this single bottleneck means
    // every code path is covered.
    pk = this.#rewriteTempPksInTransaction(pk);
    const entityConfig = this.#entities[type];
    // console.log(`ENTITY - createEntityFromPk is about to create entity object type=${type} pk=${JSON.stringify(pk)}`);
    const tempEntity = new Entity(this.#appName, entityConfig, pk);
    const store = this.#getEntityStore(type);
    const pkString = App.getPkString(pk);
    // console.log(`ENTITY - createEntityFromPk is about to store entity object type=${type} pk=${JSON.stringify(pk)}`);
    store.storeEntity(pkString, tempEntity, false, "create entity from pk");

    // Don't fetch temp PKs from the server — they're local-only until
    // the matching CreateEntity transaction completes and swaps to the
    // real PK. `#storeEntity` removes the temp-PK placeholder once the
    // real entity arrives, and `#registerTempPkMapping` records the
    // swap so any queued reads against the temp PK pick up the real
    // PK on flush. A fetch in the meantime returns 500 (server has no
    // such row) and spams the console with `failed to fetch entity`
    // errors during a multi-element paste.
    if (this.#generatedTempPks.has(`${type}|${pkString}`)) {
      return;
    }

    requestAnimationFrame(() => {
      // console.log(`ENTITY - createEntityFromPk storage of entity triggered lazy fetch&store type=${type} pk=${JSON.stringify(pk)}`);
      this.#fetchAndStoreEntity(type, pk);
    });
  }

  #syncWithServer() {
    if (this.#syncInProgress) {
    // console.log("not initiating polling because polling is already in progress");
      alert("not going to poll be cause polling is already happening");
      return;
    }
    this.#syncInProgress = true;
    // Rewrite any temp PKs that have already been reconciled to
    // real PKs in earlier sync batches. Without this, a follow-up
    // `UpdateEntity` queued AFTER the create's response arrived
    // still carries the temp PK and the server's `selectOne`
    // misses on it. JSON-stringify-clone first so the rewrite
    // doesn't mutate the queue's object graph (a Vue-reactive
    // store could have refs we'd rather not touch).
    const rawQueue = JSON.parse(JSON.stringify(this.#transactionQueue));
    this.#transactionsSent = rawQueue.map(transaction => this.#rewriteTempPksInTransaction(transaction));
    this.#transactionQueue = [];
    this.apiPost(
      `/app/${this.#appName}/transactions`,
      {
        "app_release_id": this.#releaseId,
        "last_transaction_id": this.#lastTransactionId,
        "transactions": this.#transactionsSent,
      },
      response => {
        if (!this.#lastTransactionId && response.transactionsSince > 0) {
          this.#lastTransactionIdUpdatedTimestamp = Date.now();
          this.#lastTransactionId = response.transactionsSince;
        }
        const currentMaxTransactionId = response.currentMaxTransactionId;
        if (this.#lastTransactionId > 0
          && typeof currentMaxTransactionId === "number"
          && currentMaxTransactionId - this.#lastTransactionId > MAX_TRANSACTION_LAG_BEFORE_RESET) {
          this.#lastTransactionId = currentMaxTransactionId;
          this.#lastTransactionIdUpdatedTimestamp = Date.now();
          return;
        }
        this.#processIncomingTransactions(response.transactions);
        // The server may include initQty-preloaded feeds and their fetched entities at
        // the top level, disjoint from the per-transaction output. Merge both.
        this.mergeResponsePayload(`/app/${this.#appName}/transactions`, response);
      },
      e => {
        throw new BaseFrameworkError(
          "failed to sync transactions with server — some transactions will be lost",
          { transactionsSent: this.#transactionsSent },
          e,
        );
      },
      () => {
        this.#syncInProgress = false;
        // If callers are waiting for a flush AND there are still queued
        // transactions (events fired during this sync), loop straight
        // into another sync rather than waiting for the TTL — they
        // explicitly asked for "queue drained", not "one round-trip".
        if (this.#flushResolvers.length > 0 && this.#transactionQueue.length > 0) {
          this.#syncWithServer();
          return;
        }
        if (this.#flushResolvers.length > 0) {
          const resolvers = this.#flushResolvers;
          this.#flushResolvers = [];
          resolvers.forEach(r => r());
        }
        this.#nextSyncTimerId = setTimeout(() => {
          this.#nextSyncTimerId = null;
          this.#syncWithServer();
        }, this.#ttl);
      },
    );
  }

  #importEvents() {
    Object.keys(this.#config.events).forEach(eventName => {
      this.#events[eventName] = this.#config.events[eventName];
    });
  }

  #addStandardUserEntities() {
    this.#entities.User = {
      "entityType": "User",
      "readOnly": true,
      "ignoresStandardFields": true,
      "primaryKeyFields": [
        "id",
      ],
      "actions": {},
      "fields": {
        "id": {
          "type": "int",
          "generated": true,
          "immutable": true,
        },
        "name": {
          "type": "string",
          "immutable": true,
        },
        "email": {
          "type": "email",
          "immutable": true,
        },
        "avatar": {
          "type": "image",
          "immutable": true,
        },
      },
    };
  }

  #addStandardEntities() {
    this.#addStandardUserEntities();
  }

  #importEntities() {
    this.#addStandardEntities();
    Object.keys(this.#config.entities).forEach(entityType => {
      this.#entities[entityType] = this.#config.entities[entityType];
      this.#entities[entityType].fields = Entity.mergeStandardFieldsWithEntityFields(this.#entities[entityType].fields);
    });
  }

  async #importComponents() {
    await Promise.all(Object.keys(this.#config.components).map(async componentName => {
      const templateName = this.#config.components[componentName].layout;
      const module = await import(`+/${this.#appName}/Components/${templateName}`);
      this.#components[componentName] = markRaw(module.default);
    }));
  }

  async #importPages() {
    await Promise.all(Object.keys(this.#config.pages).map(async uri => {
      const pageName = this.#config.pages[uri].template;
      const module = await import(`+/${this.#appName}/Pages/${pageName}`);
      this.#pages[pageName] = markRaw(module.default);
    }));
  }

  #fetchAndStoreEntity(type, pk) {
    // The first `getEntity(type, pk)` for an unknown pk stores an empty
    // stub entity AND schedules this fetch via `requestAnimationFrame`.
    // Between schedule and fire, the stub can get hydrated by an
    // out-of-band write (typically `mergeResponsePayload` integrating
    // the SSR-injected preload that lands milliseconds later, OR the
    // production-mode hydration from the release blob). Without this
    // guard, the rAF callback unconditionally re-fetches, wasting
    // 200+ /entities round-trips for a single page render where the
    // data is already present locally.
    //
    // The `Entity` constructor pre-initialises every non-PK field to
    // `null` so "has the field key" isn't the right signal — every
    // stub has every field name. The right signal is "any non-PK
    // field has a non-null VALUE", which only becomes true after
    // `hydrateFields` ran against a real server payload.
    const store = this.#getEntityStore(type);
    const pkString = App.getPkString(pk);
    if (store.hasEntity(pkString)) {
      const entity = store.getEntity(pkString);
      const fieldData = entity?.getFieldData?.() ?? {};
      const pkFieldNames = this.getEntityPrimaryKeyFields(type) ?? [];
      const hasHydratedField = Object.entries(fieldData).some(
        ([field, value]) => !pkFieldNames.includes(field) && value !== null && value !== undefined,
      );
      if (hasHydratedField) {
        return;
      }
    }
    this.apiGet(
      `/app/${this.#appName}/entities`,
      {
        "app_release_id": this.#releaseId,
        "requested_entities": [
          {
            "type": type,
            "pk": pk,
          },
        ],
      },
      response => {
        this.mergeResponsePayload(`/app/${this.#appName}/transactions`, response);
      },
      e => {
        throw new BaseFrameworkError(
          `failed to fetch entity ${type}-${App.getPkString(pk)}`,
          { type, pk },
          e,
        );
      },
      () => {
      },
    );
  }

  #processIncomingTransactions(transactions) {
  // @todo this does not enforce that transactions are processed in order, specifically this function can be called a second time while the previous iteration is still processing
  // maybe instead we push new incoming transactions onto a queue that might have other unprocessed transactions then we update last transaction id
  // meanwhile this method just goes through whatever is pending in order till done
    if (transactions.length > 0) {
      this.#lastTransactionId = transactions.at(-1).id;
      this.#lastTransactionIdUpdatedTimestamp = Date.now();
    }
    // Transaction-output entities arrive from the server with their
    // initQty feeds preloaded (see `expandInitQtyFeeds`). Set the
    // merge flag for the duration so `Entity.#requestInitFeeds` skips
    // its auto-trigger cascade — otherwise every transaction-returned
    // entity (Destination, Owner, …) re-fires a /feeds POST per
    // manyEntities field with initQty != 0, which on bootstrap fans
    // out to a feed-per-destination-per-field cascade. Same try/finally
    // shape as `mergeResponsePayload` so a throw mid-loop can't strand
    // the flag in `true`.
    this.#isMergingResponse = true;
    try {
      transactions.forEach(transactionData => {
        const transaction = transactionData.json;
        // @todo here is where we left off ... this.fromJson isn't a thing, do we want a transaction object or do we simply save entities
        Object.keys(transaction.output).forEach(entityType => {
          Object.keys(transaction.output[entityType]).forEach(pkJsonString => {
            let pkObject = pkJsonString;
            if (typeof pkJsonString === "string") {
              pkObject = JSON.parse(pkJsonString);
            } else if (typeof pkJsonString === "object") {
              throw new Error(`Entity PK is object ${pkJsonString}`);
            // pk = JSON.stringify(pk);
            }
            this.#storeEntity(entityType, pkObject, transaction.output[entityType][pkJsonString], true, "process incoming transactions");
          });
        });
      // maybe we need to check and see if we already got this transaction? it shouldn't happen, but maybe
      });
    } finally {
      this.#isMergingResponse = false;
    }
  }

  // TODO: EB - test that this is working
  // TODO: trigger this on update as well when necessary
  #updateRelatedEntities(localEntity, currentUser) {
    Object.keys(localEntity.fieldDefinitions).forEach(localFieldName => {
      const localFieldDef = localEntity.fieldDefinitions[localFieldName];
      if (localFieldDef.type === "oneEntity" && !localFieldDef.remoteLinkingField) {
        const remoteEntityType = localFieldDef.entity;
        if (!this.hasEntity(remoteEntityType, localEntity.getPk())) {
          return;
        }
        const remoteEntity = this.getEntity(remoteEntityType, localEntity.getPk());

        Object.keys(remoteEntity.fieldDefinitions).forEach(remoteFieldName => {
          const remoteFieldDef = remoteEntity.fieldDefinitions[remoteFieldName];

          const currentUserPk = currentUser.pk;
          if (remoteFieldDef.type === "oneEntity"
          && remoteFieldDef.entity === localEntity.type
          && remoteFieldDef.remoteLinkingField === localFieldName
          && (
            !remoteFieldDef.remoteCurrentUserField
            || localEntity.getFieldData()[remoteFieldDef.remoteCurrentUserField] === currentUserPk
          )
          ) {
            const fieldsToUpdate = {
              [remoteFieldName]: localEntity.getPk(),
            };
            this.updateSpecificEntityFields(remoteEntity.type, remoteEntity.getPk(), fieldsToUpdate);
          }
        });
      }
    });
  }

  #assertKnownEntityType(entityType) {
    if (!this.#entities[entityType]) {
      throw new Error(`"${entityType}" is not a known entity type in this app`);
    }
  }

  #getEntityDef(entityType) {
    return this.#entities[entityType];
  // return this.#config.entities[entityType];
  }

  #getFieldDef(entityType, fieldName) {
  // return this.#config.entities[entityType].fields[fieldName];
    return this.#entities[entityType].fields[fieldName];
  }

  #getEntityStore(entityType) {
    if (!this.#entityStores[entityType]) {
      const entityConfig = this.#getEntityDef(entityType);
      this.#entityStores[entityType] = new EntityStore(`app-${this.#appName}-entities-${entityType}`, entityType, entityConfig);
    }
    return this.#entityStores[entityType];
  }

}
